import { getSupabaseClient } from "../supabase/index.js";
import { defaultTenantReferenceLists } from "./tenant-reference-lists.js";
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PLAN_IDS,
  SUBSCRIPTION_TRIAL_BANNER_DAYS,
  SUBSCRIPTION_TRIAL_DAYS,
  computePendingTrainerBonus,
  computeRefundForCancel,
  getSubscriptionPlan,
  normalizeSubscriptionPlanId,
  resolveCancelEffectiveAt,
  resolveNextTrainerBonusHint,
} from "./subscription-plans.js";
import {
  ELEVATED_TENANT_ROLES_FOR_SUBSCRIPTION,
  isIndependentTrainerLogin,
} from "./trainer-ui-permissions.js";
import { emitTenantSubscriptionChanged } from "./subscription-events.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function normalizeMilestoneList(raw) {
  if (!Array.isArray(raw)) return [];
  return [
    ...new Set(
      raw
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x) && x >= 2 && x <= 5),
    ),
  ].sort((a, b) => a - b);
}

/**
 * @typedef {{
 *   planId: import('./subscription-plans.js').SubscriptionPlanId,
 *   status: 'trial'|'active'|'expired'|'canceled',
 *   trialEndsAt: string|null,
 *   trialStartedAt: string|null,
 *   currentPeriodEnd: string|null,
 *   stripeCustomerId: string|null,
 *   stripeSubscriptionId: string|null,
 *   trainerBonusMilestonesApplied: number[],
 *   cancelRequestedAt: string|null,
 *   cancelEffectiveAt: string|null,
 *   refundDays: number|null,
 *   refundEurEstimate: number|null,
 *   updatedAt: string|null,
 * }} TenantSubscription
 *
 * @typedef {{
 *   planId: import('./subscription-plans.js').SubscriptionPlanId,
 *   status: 'trial'|'active'|'expired'|'canceled',
 *   trialEndsAt: string|null,
 *   trialStartedAt: string|null,
 *   currentPeriodEnd: string|null,
 *   features: { billing: boolean, analytics: boolean, clubApps: boolean, clubInsights: boolean },
 *   canWrite: boolean,
 *   showTrialBanner: boolean,
 *   showExpiredGate: boolean,
 *   daysLeftInTrial: number|null,
 *   trainerBonusMilestonesApplied: number[],
 *   cancelRequestedAt: string|null,
 *   cancelEffectiveAt: string|null,
 *   refundDays: number|null,
 *   refundEurEstimate: number|null,
 *   nextTrainerBonus: { nextTrainerCount: number, trainersNeeded: number, bonusDays: number }|null,
 * }} SubscriptionAccess
 */

/** @param {Date} [now] */
export function defaultTenantSubscription(now = new Date()) {
  const trialEnds = new Date(now.getTime());
  trialEnds.setUTCDate(trialEnds.getUTCDate() + SUBSCRIPTION_TRIAL_DAYS);
  return {
    planId: /** @type {const} */ ("pro"),
    status: /** @type {const} */ ("trial"),
    trialEndsAt: trialEnds.toISOString(),
    trialStartedAt: now.toISOString(),
    currentPeriodEnd: null,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    trainerBonusMilestonesApplied: [],
    cancelRequestedAt: null,
    cancelEffectiveAt: null,
    refundDays: null,
    refundEurEstimate: null,
    updatedAt: now.toISOString(),
  };
}

/** @param {unknown} raw */
export function normalizeTenantSubscription(raw) {
  const base = defaultTenantSubscription();
  if (!raw || typeof raw !== "object") return base;
  const statusRaw = String(raw.status || "")
    .trim()
    .toLowerCase();
  const status =
    statusRaw === "active" ||
    statusRaw === "expired" ||
    statusRaw === "canceled" ||
    statusRaw === "trial"
      ? statusRaw
      : "trial";
  return {
    planId: normalizeSubscriptionPlanId(raw.planId),
    status,
    trialEndsAt: raw.trialEndsAt ? asStr(raw.trialEndsAt) || null : null,
    trialStartedAt: raw.trialStartedAt
      ? asStr(raw.trialStartedAt) || null
      : base.trialStartedAt,
    currentPeriodEnd: raw.currentPeriodEnd
      ? asStr(raw.currentPeriodEnd) || null
      : null,
    stripeCustomerId: raw.stripeCustomerId
      ? asStr(raw.stripeCustomerId) || null
      : null,
    stripeSubscriptionId: raw.stripeSubscriptionId
      ? asStr(raw.stripeSubscriptionId) || null
      : null,
    trainerBonusMilestonesApplied: normalizeMilestoneList(
      raw.trainerBonusMilestonesApplied,
    ),
    cancelRequestedAt: raw.cancelRequestedAt
      ? asStr(raw.cancelRequestedAt) || null
      : null,
    cancelEffectiveAt: raw.cancelEffectiveAt
      ? asStr(raw.cancelEffectiveAt) || null
      : null,
    refundDays:
      raw.refundDays == null || raw.refundDays === ""
        ? null
        : Number(raw.refundDays),
    refundEurEstimate:
      raw.refundEurEstimate == null || raw.refundEurEstimate === ""
        ? null
        : Number(raw.refundEurEstimate),
    updatedAt: raw.updatedAt ? asStr(raw.updatedAt) || null : null,
  };
}

/**
 * @param {TenantSubscription} subscription
 * @param {number} trainerCount
 * @param {Date} [now]
 */
export function applyTrainerTrialBonuses(
  subscription,
  trainerCount,
  now = new Date(),
) {
  const sub = normalizeTenantSubscription(subscription);
  if (sub.status !== "trial") {
    return {
      subscription: sub,
      appliedMilestones: [],
      bonusDays: 0,
      changed: false,
    };
  }
  const pending = computePendingTrainerBonus(
    trainerCount,
    sub.trainerBonusMilestonesApplied,
  );
  if (!pending.milestones.length || pending.bonusDays <= 0) {
    return {
      subscription: sub,
      appliedMilestones: [],
      bonusDays: 0,
      changed: false,
    };
  }
  const baseEndsMs = sub.trialEndsAt
    ? Date.parse(sub.trialEndsAt)
    : now.getTime();
  const startMs = Number.isFinite(baseEndsMs) ? baseEndsMs : now.getTime();
  const nextEnds = new Date(startMs);
  nextEnds.setUTCDate(nextEnds.getUTCDate() + pending.bonusDays);
  return {
    subscription: {
      ...sub,
      trialEndsAt: nextEnds.toISOString(),
      trainerBonusMilestonesApplied: normalizeMilestoneList([
        ...sub.trainerBonusMilestonesApplied,
        ...pending.milestones,
      ]),
      updatedAt: now.toISOString(),
    },
    appliedMilestones: pending.milestones,
    bonusDays: pending.bonusDays,
    changed: true,
  };
}

/**
 * @param {TenantSubscription|null|undefined} subscription
 * @param {Date} [now]
 * @param {{ trainerCount?: number }} [opts]
 */
export function resolveSubscriptionAccess(
  subscription,
  now = new Date(),
  opts = {},
) {
  const sub = normalizeTenantSubscription(subscription);
  const nowMs = now.getTime();
  let status = sub.status;
  let daysLeftInTrial = null;
  let canWrite = false;

  if (status === "canceled") {
    const effMs = sub.cancelEffectiveAt
      ? Date.parse(sub.cancelEffectiveAt)
      : Number.NaN;
    if (Number.isFinite(effMs) && effMs >= nowMs) {
      canWrite = true;
    } else {
      status = "expired";
      canWrite = false;
    }
  } else if (status === "trial") {
    const endsMs = sub.trialEndsAt ? Date.parse(sub.trialEndsAt) : Number.NaN;
    if (Number.isFinite(endsMs)) {
      daysLeftInTrial = Math.ceil((endsMs - nowMs) / 86400000);
      if (endsMs < nowMs) {
        status = "expired";
        daysLeftInTrial = 0;
        canWrite = false;
      } else {
        canWrite = true;
      }
    } else {
      canWrite = true;
    }
  } else if (status === "active") {
    canWrite = true;
  }

  const plan = getSubscriptionPlan(sub.planId);
  /** @type {{ billing: boolean, analytics: boolean, clubApps: boolean, clubInsights: boolean }} */
  const features = { ...plan.features };
  if (status === "expired" || !canWrite) {
    features.billing = false;
    features.analytics = false;
    features.clubApps = false;
    features.clubInsights = false;
  }

  const showTrialBanner =
    status === "trial" &&
    daysLeftInTrial != null &&
    daysLeftInTrial <= SUBSCRIPTION_TRIAL_BANNER_DAYS;

  const trainerCount = Math.max(0, Math.floor(Number(opts.trainerCount) || 0));
  const nextTrainerBonus =
    status === "trial"
      ? resolveNextTrainerBonusHint(
          trainerCount,
          sub.trainerBonusMilestonesApplied,
        )
      : null;

  return {
    planId: sub.planId,
    status,
    trialEndsAt: sub.trialEndsAt,
    trialStartedAt: sub.trialStartedAt,
    currentPeriodEnd: sub.currentPeriodEnd,
    features,
    canWrite,
    showTrialBanner,
    showExpiredGate: status === "expired",
    daysLeftInTrial: status === "trial" ? daysLeftInTrial : null,
    trainerBonusMilestonesApplied: sub.trainerBonusMilestonesApplied,
    cancelRequestedAt: sub.cancelRequestedAt,
    cancelEffectiveAt: sub.cancelEffectiveAt,
    refundDays: sub.refundDays,
    refundEurEstimate: sub.refundEurEstimate,
    nextTrainerBonus,
  };
}

/**
 * @param {{ mode?: string, trainerRow?: object|null, tenantRoles?: string[] }} input
 */
export function canManageTenantSubscription({
  mode,
  trainerRow,
  tenantRoles = [],
} = {}) {
  const viewerMode = asStr(mode);
  if (viewerMode === "coordinator" || viewerMode === "planner") return true;
  if (
    isIndependentTrainerLogin({ mode: viewerMode, trainerRow, tenantRoles })
  ) {
    return true;
  }
  const roles = (Array.isArray(tenantRoles) ? tenantRoles : [])
    .map((r) =>
      String(r || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
  for (const role of roles) {
    if (ELEVATED_TENANT_ROLES_FOR_SUBSCRIPTION.has(role)) return true;
  }
  return false;
}

/**
 * @param {string} tenantId
 */
export async function loadTenantSubscription(tenantId) {
  const tid = asStr(tenantId);
  if (!tid) {
    return {
      ok: true,
      data: defaultTenantSubscription(),
      reason: "missing-tenant",
    };
  }

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("trainerstunden_tenant_reference_lists")
      .select("settings")
      .eq("tenant_id", tid)
      .maybeSingle();

    if (error) {
      const code = String(error?.code || "");
      if (code === "42P01" || code === "42703") {
        return {
          ok: true,
          data: defaultTenantSubscription(),
          reason: "schema-missing",
        };
      }
      return { ok: false, error };
    }

    const settings =
      data?.settings && typeof data.settings === "object" ? data.settings : {};
    if (!settings.saasSubscription) {
      return {
        ok: true,
        data: defaultTenantSubscription(),
        reason: "missing-subscription",
        needsPersist: true,
      };
    }

    return {
      ok: true,
      data: normalizeTenantSubscription(settings.saasSubscription),
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 * @param {Partial<TenantSubscription> & { planId?: string, status?: string }} patch
 */
export async function saveTenantSubscription(tenantId, patch) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: false, error: "missing-tenant" };

  const loaded = await loadTenantSubscription(tid);
  const previous =
    loaded.ok && loaded.data ? loaded.data : defaultTenantSubscription();

  const payload = normalizeTenantSubscription({
    ...previous,
    ...patch,
    updatedAt: new Date().toISOString(),
  });

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data: existing, error: readError } = await supabase
      .from("trainerstunden_tenant_reference_lists")
      .select("tenant_id, lists, settings")
      .eq("tenant_id", tid)
      .maybeSingle();

    if (readError) {
      const code = String(readError?.code || "");
      if (code === "42P01" || code === "42703")
        return { ok: false, error: "schema-missing" };
      return { ok: false, error: readError };
    }

    const previousSettings =
      existing?.settings && typeof existing.settings === "object"
        ? existing.settings
        : {};

    const row = {
      tenant_id: tid,
      settings: { ...previousSettings, saasSubscription: payload },
      updated_at: new Date().toISOString(),
      lists: existing?.lists || defaultTenantReferenceLists(),
    };

    const { error } = await supabase
      .from("trainerstunden_tenant_reference_lists")
      .upsert(row, { onConflict: "tenant_id" });

    if (error) {
      const code = String(error?.code || "");
      if (code === "42P01" || code === "42703")
        return { ok: false, error: "schema-missing" };
      return { ok: false, error };
    }

    return { ok: true, data: payload };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 */
export async function ensureTenantSubscription(tenantId) {
  const loaded = await loadTenantSubscription(tenantId);
  if (!loaded.ok) return loaded;
  if (!loaded.needsPersist) return loaded;
  return saveTenantSubscription(tenantId, loaded.data);
}

/**
 * @param {string} tenantId
 * @param {string} planId
 */
export async function activateTenantSubscriptionPlan(tenantId, planId) {
  const id = normalizeSubscriptionPlanId(planId);
  const plan = SUBSCRIPTION_PLANS[id];
  const periodEnd = new Date();
  periodEnd.setUTCMonth(periodEnd.getUTCMonth() + 1);
  return saveTenantSubscription(tenantId, {
    planId: plan.id,
    status: "active",
    trialEndsAt: null,
    currentPeriodEnd: periodEnd.toISOString(),
    cancelRequestedAt: null,
    cancelEffectiveAt: null,
    refundDays: null,
    refundEurEstimate: null,
  });
}

/**
 * @param {string} tenantId
 * @param {number} trainerCount
 */
export async function syncTenantSubscriptionTrainerBonuses(
  tenantId,
  trainerCount,
) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: false, error: "missing-tenant" };
  const ensured = await ensureTenantSubscription(tid);
  if (!ensured.ok) return ensured;
  const applied = applyTrainerTrialBonuses(ensured.data, trainerCount);
  if (!applied.changed) {
    return { ok: true, data: applied.subscription, changed: false };
  }
  const saved = await saveTenantSubscription(tid, applied.subscription);
  if (saved.ok) {
    emitTenantSubscriptionChanged(tid, saved.data);
  }
  return { ...saved, changed: true, bonusDays: applied.bonusDays };
}

/**
 * @param {string} tenantId
 * @param {Date} [now]
 */
export async function requestTenantSubscriptionCancel(
  tenantId,
  now = new Date(),
) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: false, error: "missing-tenant" };
  const ensured = await ensureTenantSubscription(tid);
  if (!ensured.ok) return ensured;
  const sub = normalizeTenantSubscription(ensured.data);
  if (sub.status !== "trial" && sub.status !== "active") {
    return { ok: false, error: "not-cancellable", data: sub };
  }
  if (sub.cancelRequestedAt && sub.cancelEffectiveAt) {
    return { ok: true, data: sub, alreadyCanceled: true };
  }

  const cancelEffectiveAt = resolveCancelEffectiveAt(now);
  let refundDays = null;
  let refundEurEstimate = null;
  let trialEndsAt = sub.trialEndsAt;
  let currentPeriodEnd = sub.currentPeriodEnd;

  if (sub.status === "active") {
    const refund = computeRefundForCancel({
      planId: sub.planId,
      currentPeriodEnd: sub.currentPeriodEnd,
      cancelEffectiveAt,
    });
    refundDays = refund.refundDays;
    refundEurEstimate = refund.refundEurEstimate;
    currentPeriodEnd = cancelEffectiveAt;
  } else {
    const trialMs = sub.trialEndsAt ? Date.parse(sub.trialEndsAt) : Number.NaN;
    const effMs = Date.parse(cancelEffectiveAt);
    if (Number.isFinite(trialMs) && Number.isFinite(effMs)) {
      trialEndsAt = new Date(Math.min(trialMs, effMs)).toISOString();
    } else {
      trialEndsAt = cancelEffectiveAt;
    }
  }

  const saved = await saveTenantSubscription(tid, {
    ...sub,
    status: "canceled",
    cancelRequestedAt: now.toISOString(),
    cancelEffectiveAt,
    refundDays,
    refundEurEstimate,
    trialEndsAt,
    currentPeriodEnd,
  });
  if (saved.ok) {
    emitTenantSubscriptionChanged(tid, saved.data);
  }
  return saved;
}

export { SUBSCRIPTION_PLANS, SUBSCRIPTION_PLAN_IDS };
